import { useState, useEffect, useMemo, useRef } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  DatePicker,
  Row,
  Col,
  message,
  Spin,
} from "antd";
import dayjs from "dayjs";
import {
  addInvoice,
  getInvoiceByID,
  getCustomersForReceipts,
  getallProjectForReceipts,
  getLastInvoiceNumber,
} from "../../utils/API";
import { useNavigate, useParams } from "react-router-dom";
import { downloadInvoicePdf } from "../../utils/invoicePdf";
import { ArrowLeftOutlined } from "@ant-design/icons";
import debounce from "lodash/debounce";
import {
  markInvoiceAutoDraftPending,
  markInvoiceAutoDraftCompleted,
  clearInvoiceAutoDraftPending,
  resolveInvoiceNumberForCreate,
  getNextSavedInvoicePreviewSequence,
} from "../../utils/invoiceNumber";

// Currency interface
interface CurrencyState {
  selectedCurrency: string;
  rate: number | null;
  originalValue: number | null;
  convertedValue: number | null;
}

const currencies = [
  { code: "USD", name: "US Dollar", symbol: "$", rate: null },
  { code: "EUR", name: "Euro", symbol: "€", rate: null },
];

const isValidObjectId = (id: string): boolean => {
  return /^[0-9a-fA-F]{24}$/.test(id);
};

const hasItemContent = (item: any) => {
  if (!item || typeof item !== "object") {
    return false;
  }

  return [
    item.description,
    item.hsn,
    item.gst,
    item.originalAmount,
    item.amount,
  ].some((value) => {
    if (value === 0 || value === "0") {
      return true;
    }
    return String(value ?? "").trim() !== "";
  });
};

const hasMeaningfulInvoiceData = (values: any) => {
  if (!values || typeof values !== "object") {
    return false;
  }

  const hasMainDetails = [
    values.customer_name,
    values.address,
    values.place_of_supply,
    values.project,
    values.invoice_date,
    values.due_date,
    values.payment_link,
    values.email,
  ].some((value) => String(value ?? "").trim() !== "");

  const hasItems = (values.items || []).some((item: any) => hasItemContent(item));

  return hasMainDetails || hasItems;
};

const CopyInvoicePage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();

  const CUSTOM_VALUE_PREFIX = "custom_";

  const [isLoading, setIsLoading] = useState(false);
  const [submitMode, setSubmitMode] = useState<"save" | "draft" | null>(null);
  const [isFetchingInvoice, setIsFetchingInvoice] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [invoiceSeq, setInvoiceSeq] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [isManualCustomerEntry, setIsManualCustomerEntry] = useState(false);
  const [currencyState, setCurrencyState] = useState<CurrencyState>({
    selectedCurrency: "INR",
    rate: null,
    originalValue: null,
    convertedValue: null,
  });

  // Refs to track if we've already loaded the invoice
  const invoiceLoadedRef = useRef(false);
  const initialDataLoadedRef = useRef(false);
  const latestValuesRef = useRef<any>({});
  const skipAutoDraftRef = useRef(false);
  const isAutoDraftingRef = useRef(false);
  const invoiceSeqRef = useRef(1);
  const selectedCurrencyRef = useRef<string | undefined>(undefined);
  const exchangeRateRef = useRef<number | null>(null);
  
  // ===== STYLE =====
  const sectionStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e5e7eb",
    padding: "24px",
    width: "100%",
  };

  // ===== INVOICE NUMBER =====
  const prefix = "INV";
  const year = dayjs().format("YYYY");

  const formatSequence = (num: number) =>
    num.toString().padStart(4, "0");

  const generateInvoiceNumber = (seq: number) =>
    `${prefix}-${year}-${formatSequence(seq)}`;

  const buildInvoiceData = async (values: any, invoiceStatus: string) => {
    const invoiceNumber =
      invoiceStatus === "drafted"
        ? (await resolveInvoiceNumberForCreate("drafted")).invoiceNumber
        : (await resolveInvoiceNumberForCreate("sent")).invoiceNumber;
    let customCustomerName = null;

    let customerId = null;
    const customerName = values.customer_name;

    if (
      typeof customerName === "string" &&
      customerName.startsWith(CUSTOM_VALUE_PREFIX)
    ) {
      customCustomerName = customerName.replace(CUSTOM_VALUE_PREFIX, "");
    } else if (
      typeof customerName === "string" &&
      isValidObjectId(customerName)
    ) {
      customerId = customerName;
    } else if (typeof customerName === "string") {
      customCustomerName = customerName;
    }

    const baseValues = { ...values };
    delete baseValues._id;
    delete baseValues.createdAt;
    delete baseValues.updatedAt;
    delete baseValues.__v;

    const cleanedValues: any = {
      ...baseValues,
      customer_name: customerId,
      custom_customer_name: customCustomerName,
      invoice_number: invoiceNumber,
      invoice_status: invoiceStatus,
      invoice_date: values.invoice_date
        ? dayjs(values.invoice_date).format("DD-MM-YYYY")
        : null,
      due_date: values.due_date
        ? dayjs(values.due_date).format("DD-MM-YYYY")
        : null,
      taxable_amount: Number(values.taxable_amount || 0),
      grand_total: Number(values.grand_total || 0),
      cgst_amount: Number(values.cgst_amount || 0),
      sgst_amount: Number(values.sgst_amount || 0),
      igst_amount: Number(values.igst_amount || 0),
      cgst_percent: Number(values.cgst_percent || 0),
      sgst_percent: Number(values.sgst_percent || 0),
      igst_percent: Number(values.igst_percent || 0),
      amount: Number(values.amount || 0),
      hsn: Number(values.hsn || 0),
      gst: Number(values.gst || 0),
      payment_link: values.payment_link || "",
      email: values.email || "",
      items: (values.items || [])
        .filter((item: any) => hasItemContent(item))
        .map((item: any, index: number) => ({
          ...item,
          amount: Number(item.amount || 0),
          originalAmount: item.originalAmount ? Number(item.originalAmount) : Number(item.amount || 0),
          hsn: Number(item.hsn || 0),
          gst: Number(item.gst || 0),
          sr_no: index + 1,
        })),
    };

    const currentCurrency = values.currency;
    if (
      currentCurrency !== "INR" &&
      currentCurrency &&
      exchangeRateRef.current
    ) {
      const totalOriginalAmount = (values.items || []).reduce((sum: number, item: any) =>
        sum + (Number(item.originalAmount) || 0), 0) || 0;

      cleanedValues.currency_conversion = {
        original_currency: selectedCurrencyRef.current || currentCurrency,
        original_amount: totalOriginalAmount,
        exchange_rate: exchangeRateRef.current,
        converted_inr_amount: Number(values.grand_total || 0) * Number(exchangeRateRef.current || 0),
      };
      cleanedValues.currency = currentCurrency;
    } else {
      cleanedValues.currency = "INR";
      cleanedValues.currency_conversion = null;
    }

    return cleanedValues;
  };

  // ===== WATCHERS =====
  const items = Form.useWatch("items", form);
  const cgstPercent = Form.useWatch("cgst_percent", form);
  const sgstPercent = Form.useWatch("sgst_percent", form);
  const igstPercent = Form.useWatch("igst_percent", form);
  const selectedCurrency = Form.useWatch("currency", form);
  const invoiceDate = Form.useWatch("invoice_date", form);
  const invoiceStatus = Form.useWatch("invoice_status", form);
  const currentCustomerValue = Form.useWatch("customer_name", form);

  const disablePastDates = () => false;

  const disableDueDates = (current: any) => {
    if (!current || !invoiceDate) return false;
    return current.startOf("day").isBefore(dayjs(invoiceDate).startOf("day"));
  };

  useEffect(() => {
    if (!invoiceDate) return;
    const nextDueDate = dayjs(invoiceDate).add(3, "day").startOf("day");
    const currentDueDate = form.getFieldValue("due_date");

    if (!currentDueDate || !dayjs(currentDueDate).isSame(nextDueDate, "day")) {
      form.setFieldsValue({ due_date: nextDueDate });
    }
  }, [invoiceDate, form]);

  const round2 = (num: number) =>
    Math.round((num + Number.EPSILON) * 100) / 100;

  // ===== FETCH CUSTOMERS FUNCTION =====
  const fetchCustomers = async (searchText = "") => {
    try {
      const res: any = await getCustomersForReceipts(searchText, "", true);
      if (res?.data) {
        setCustomers(res.data.filter((c: any) => c.isActive));
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  // ===== CUSTOMER OPTIONS WITH MANUAL ENTRY =====
  const customerOptions = useMemo(() => {
    const list = customers.map((customer: any) => ({
      label: customer.name,
      value: customer._id,
    }));

    const typed = customerSearchText.trim();
    const hasExactMatch = customers.some(
      (customer: any) =>
        (customer.name || "").toLowerCase() === typed.toLowerCase(),
    );

    if (typed && !hasExactMatch) {
      list.unshift({
        label: `${typed}`,
        value: `${CUSTOM_VALUE_PREFIX}${typed}`,
      });
    }

    if (
      typeof currentCustomerValue === "string" &&
      currentCustomerValue.startsWith(CUSTOM_VALUE_PREFIX) &&
      !list.some((option) => option.value === currentCustomerValue)
    ) {
      list.unshift({
        label: currentCustomerValue.replace(CUSTOM_VALUE_PREFIX, ""),
        value: currentCustomerValue,
      });
    }

    return list;
  }, [customers, customerSearchText, currentCustomerValue]);

  // ===== DEBOUNCED SEARCH =====
  const debouncedSearchCustomers = useMemo(
    () =>
      debounce((searchText: string) => {
        fetchCustomers(searchText);
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearchCustomers.cancel();
    };
  }, [debouncedSearchCustomers]);

  useEffect(() => {
    invoiceSeqRef.current = invoiceSeq;
  }, [invoiceSeq]);

  useEffect(() => {
    selectedCurrencyRef.current = selectedCurrency;
  }, [selectedCurrency]);

  useEffect(() => {
    exchangeRateRef.current = currencyState.rate;
  }, [currencyState.rate]);

  useEffect(() => {
    latestValuesRef.current = form.getFieldsValue(true);
  }, [form]);

  useEffect(() => {
    return () => {
      if (
        skipAutoDraftRef.current ||
        isAutoDraftingRef.current ||
        !form.isFieldsTouched(true)
      ) {
        return;
      }

      const values = latestValuesRef.current || form.getFieldsValue(true);
      if (!hasMeaningfulInvoiceData(values)) {
        return;
      }

      isAutoDraftingRef.current = true;

      void (async () => {
        markInvoiceAutoDraftPending();

        try {
          const invoiceData = await buildInvoiceData(values, "drafted");
          await addInvoice(invoiceData);
          markInvoiceAutoDraftCompleted();
        } catch (error) {
          clearInvoiceAutoDraftPending();
          console.error("Auto draft save failed:", error);
        }
      })();
    };
  }, [form]);

  // ===== CONVERT AMOUNT FUNCTION =====
  const convertAmount = (originalAmount: number, rate: number | null): number => {
    if (selectedCurrency === "INR" || !selectedCurrency || !rate) {
      return originalAmount;
    }
    return originalAmount * rate;
  };

  // ===== UPDATE SINGLE ITEM AMOUNT =====
  const updateItemAmount = (item: any, rate: number | null) => {
    const originalAmount = item.originalAmount || item.amount;
    const convertedAmount = selectedCurrency === "INR" 
      ? originalAmount 
      : convertAmount(originalAmount, rate);
    
    return {
      ...item,
      originalAmount: originalAmount,
      amount: convertedAmount,
    };
  };

  // ===== UPDATE ALL ITEMS BASED ON CURRENT RATE =====
  const updateAllItems = (rate: number | null) => {
    const currentItems = form.getFieldValue("items") || [];
    const updatedItems = currentItems.map((item: any) => 
      updateItemAmount(item, rate)
    );
    form.setFieldsValue({ items: updatedItems });
  };

  // ===== CURRENCY HANDLERS =====
  const handleCurrencyChange = (currencyCode: string) => {
    const currency = currencies.find(c => c.code === currencyCode);
    
    if (currencyCode === "INR") {
      const currentItems = form.getFieldValue("items") || [];
      const updatedItems = currentItems.map((item: any) => {
        const visibleAmount = Number(item.originalAmount || item.amount || 0);
        return {
          ...item,
          originalAmount: visibleAmount,
          amount: visibleAmount,
        };
      });
      form.setFieldsValue({ items: updatedItems });
      
      setCurrencyState({
        selectedCurrency: "INR",
        rate: null,
        originalValue: null,
        convertedValue: null,
      });
      form.setFieldsValue({
        exchange_rate: undefined,
        cgst_percent: 9.0,
        sgst_percent: 9.0,
        igst_percent: 18,
      });
    } else if (currency) {
      const currentItems = form.getFieldValue("items") || [];
      const updatedItems = currentItems.map((item: any) => {
        const visibleAmount = Number(item.originalAmount || item.amount || 0);
        const convertedInrAmount = currency.rate && currency.rate > 0
          ? visibleAmount * currency.rate
          : visibleAmount;
        
        return {
          ...item,
          originalAmount: visibleAmount,
          amount: convertedInrAmount,
        };
      });
      form.setFieldsValue({ items: updatedItems });
      
      setCurrencyState({
        selectedCurrency: currencyCode,
        rate: currency.rate || null,
        originalValue: null,
        convertedValue: null,
      });
      form.setFieldsValue({
        cgst_percent: 0,
        sgst_percent: 0,
        igst_percent: 0,
        cgst_amount: 0,
        sgst_amount: 0,
        igst_amount: 0,
      });
    }
  };

  const handleCurrencyRateChange = (value: string) => {
    let val = parseFloat(value);
    if (isNaN(val)) val = 0;
    
    setCurrencyState(prev => ({
      ...prev,
      rate: val,
      convertedValue: prev.originalValue || null,
    }));
    
    const currentItems = form.getFieldValue("items") || [];
    const updatedItems = currentItems.map((item: any) => {
      const originalAmount = Number(item.originalAmount || 0);
      const convertedInrAmount = val > 0 ? originalAmount * val : originalAmount;
      return {
        ...item,
        originalAmount,
        amount: convertedInrAmount,
      };
    });
    form.setFieldsValue({ items: updatedItems });
  };

  // ===== HANDLE ITEM AMOUNT CHANGE =====
  const handleItemAmountChange = (index: number, originalAmount: number) => {
    const currentItems = form.getFieldValue("items") || [];
    const convertedAmount = convertAmount(originalAmount, currencyState.rate);
    
    const updatedItems = [...currentItems];
    updatedItems[index] = {
      ...updatedItems[index],
      originalAmount: originalAmount,
      amount: convertedAmount,
    };
    
    form.setFieldsValue({ items: updatedItems });
  };
  // ===== AUTO GST + ROUND OFF =====
  useEffect(() => {
    if (isInitialLoad) return;

    const safeItems = Array.isArray(items) ? items : [];

    const amountKey =
      selectedCurrency && selectedCurrency !== "INR" ? "originalAmount" : "amount";
    const total = round2(
      safeItems.reduce(
        (sum, item) => sum + Number(item?.[amountKey] || item?.amount || 0),
        0
      )
    );

    const cgst = Number(cgstPercent || 0);
    const sgst = Number(sgstPercent || 0);
    const igst = Number(igstPercent || 0);

    let cgstAmount = 0;
    let sgstAmount = 0;
    let igstAmount = 0;

    if (cgst > 0 || sgst > 0) {
      cgstAmount = round2((total * cgst) / 100);
      sgstAmount = round2((total * sgst) / 100);
    } else if (igst > 0) {
      igstAmount = round2((total * igst) / 100);
    }

    const calculatedGrand = total + cgstAmount + sgstAmount + igstAmount;
    const roundedGrand = Math.round(calculatedGrand);
    const roundOff = round2(roundedGrand - calculatedGrand);
    const roundOffDisplay = roundOff > 0 ? `+${roundOff.toFixed(2)}` : roundOff.toFixed(2);

    form.setFieldsValue({
      taxable_amount: total,
      cgst_amount: cgstAmount,
      sgst_amount: sgstAmount,
      igst_amount: igstAmount,
      round_off: roundOffDisplay,
      grand_total: roundedGrand,
    });
  }, [items, cgstPercent, sgstPercent, igstPercent, isInitialLoad, form]);

  // ===== GET NEW INVOICE NUMBER =====
  useEffect(() => {
    const fetchNumber = async () => {
      try {
        const nextSeq = await getNextSavedInvoicePreviewSequence();
        setInvoiceSeq(nextSeq);
      } catch {
        setInvoiceSeq(1);
      }
    };

    fetchNumber();
  }, []);

  // ===== FETCH PROJECTS =====
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res: any = await getallProjectForReceipts("", "", true);
        if (res?.data) {
          setProjects(res.data.filter((p: any) => p.isActive));
        }
      } catch (error) {
        console.error("Failed to fetch projects:", error);
      }
    };
    
    fetchProjects();
  }, []);

  // ===== FETCH CUSTOMERS INITIALLY =====
  useEffect(() => {
    fetchCustomers("");
  }, []);

  // ===== FETCH INVOICE DATA - ONLY ONCE =====
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id || invoiceLoadedRef.current) return;

      setIsFetchingInvoice(true);

      try {
        const res: any = await getInvoiceByID(id);
        const data = res?.data?.data || res?.data || res;

        if (!data || Object.keys(data).length === 0) {
          message.error("Invoice not found");
          return;
        }

        // Set currency state if currency conversion exists
        if (data.currency_conversion) {
          setCurrencyState({
            selectedCurrency: data.currency_conversion.original_currency,
            rate: data.currency_conversion.exchange_rate,
            originalValue: data.currency_conversion.original_amount,
            convertedValue: data.currency_conversion.converted_inr_amount,
          });
        } else if (data.currency && data.currency !== 'INR') {
          const currency = currencies.find(c => c.code === data.currency);
          setCurrencyState({
            selectedCurrency: data.currency,
            rate: currency?.rate || null,
            originalValue: null,
            convertedValue: null,
          });
        }

        // Helper function for safe date parsing
        const parseDateSafely = (dateVal: any) => {
          if (!dateVal) return dayjs();

          if (typeof dateVal === "number") {
            const parsed = dayjs(dateVal);
            if (parsed.isValid()) return parsed;
          }

          if (typeof dateVal === "string") {
            if (dateVal.includes("-")) {
              const parts = dateVal.split("-");
              if (parts.length === 3 && parts[0].length === 2) {
                const parsed = dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`);
                if (parsed.isValid()) return parsed;
              }
            }

            const parsed = dayjs(dateVal);
            if (parsed.isValid()) return parsed;
          }

          return dayjs();
        };

        // Get customer value - handle both object and string
        let customerValue = null;
      
        if (data.custom_customer_name) {
          // Manual customer name
          customerValue = `${CUSTOM_VALUE_PREFIX}${data.custom_customer_name}`;
          setIsManualCustomerEntry(true);
        } else if (data.customer_name) {
          if (typeof data.customer_name === 'object' && data.customer_name._id) {
            // Populated customer object
            customerValue = data.customer_name._id;
          } else if (typeof data.customer_name === 'string') {
            // Check if it's a valid ObjectId
            if (isValidObjectId(data.customer_name)) {
              customerValue = data.customer_name;
            } else {
              // It's a custom name not in database
              customerValue = `${CUSTOM_VALUE_PREFIX}${data.customer_name}`;
              setIsManualCustomerEntry(true);
            }
          }
        }

        // Process items
        let processedItems = [];
        if (Array.isArray(data.items) && data.items.length > 0) {
          processedItems = data.items.map((item: any) => {
            if (data.currency_conversion && data.currency !== 'INR') {
              const originalAmount = data.currency_conversion.exchange_rate 
                ? item.amount / data.currency_conversion.exchange_rate 
                : item.amount;
              
              return {
                ...item,
                originalAmount: originalAmount,
                amount: item.amount,
              };
            }
            return {
              ...item,
              originalAmount: item.originalAmount || item.amount,
              amount: item.amount,
            };
          });
        } else {
          processedItems = [{
            description: data.description || "",
            hsn: data.hsn || "",
            gst: data.gst || "",
            amount: data.amount || 0,
            originalAmount: data.amount || 0,
          }];
        }

        // Set form values — Copy invoice uses TODAY's date, not the original
        const today = dayjs();
        const autoDueDate = today.add(3, "day");
        form.setFieldsValue({
          ...data,
          customer_name: customerValue,
          items: processedItems,
          invoice_date: today,
          due_date: autoDueDate,
          currency: data.currency || "INR",
          invoice_status: data.invoice_status || "drafted",
          payment_link: data.payment_link || "",
          email: data.email || "",
        });

        invoiceLoadedRef.current = true;
        setIsInitialLoad(false);
      } catch (err) {
        console.error(err);
        message.error("Failed to load invoice");
      } finally {
        setIsFetchingInvoice(false);
      }
    };

    // Wait for customers to be loaded before fetching invoice
    if (!initialDataLoadedRef.current && customers.length > 0) {
      initialDataLoadedRef.current = true;
      fetchInvoice();
    }
  }, [id, customers]);

  // ===== SUBMIT COPY =====
  const submitCopyInvoice = async (
    values: any,
    invoiceStatus: "sent" | "drafted",
    mode: "save" | "draft"
  ) => {
    setIsLoading(true);
    setSubmitMode(mode);
    skipAutoDraftRef.current = true;

    try {
      const cleanedValues = await buildInvoiceData(values, invoiceStatus);
      await addInvoice(cleanedValues);
      message.success(
        invoiceStatus === "drafted"
          ? "Invoice draft saved successfully!"
          : "Invoice copied successfully!"
      );
      navigate("/invoice");
    } catch (error) {
      console.error("Copy error:", error);
      skipAutoDraftRef.current = false;
      message.error("Copy Failed");
    } finally {
      setIsLoading(false);
      setSubmitMode(null);
    }
  };

  const onFinish = async (values: any) => {
    await submitCopyInvoice(values, "sent", "save");
  };

  const handleSaveDraft = async () => {
    const values = form.getFieldsValue(true);
    await submitCopyInvoice(values, "drafted", "draft");
  };

  // ===== PREVIEW BUTTON =====
  const handlePreview = async () => {
    try {
      const values = await form.validateFields();

      let customerName = values.customer_name;
      if (
        typeof customerName === "string" &&
        customerName.startsWith(CUSTOM_VALUE_PREFIX)
      ) {
        customerName = customerName.replace(CUSTOM_VALUE_PREFIX, "");
      } else if (customerName && customers.length > 0) {
        const selectedCustomer = customers.find(
          (c) => c._id === values.customer_name
        );
        customerName = selectedCustomer?.name || "-";
      }

      const previewData = {
        ...values,
        items: values.items || [],
        customer_name: customerName,
        invoice_number: generateInvoiceNumber(invoiceSeq),
        invoice_date: values.invoice_date
          ? dayjs(values.invoice_date).format("DD-MM-YYYY")
          : null,
        invoice_status: values.invoice_status || "drafted",
        payment_link: values.payment_link || "",
      };

      downloadInvoicePdf(previewData, "preview");
    } catch (error) {
      console.error(error);
      message.error("Please fill required fields");
    }
  };

  // Get status tag color
  const getStatusTagColor = (status: string) => {
    switch(status) {
      case 'drafted':
        return 'default';
      case 'sent':
        return 'processing';
      case 'cleared':
        return 'success';
      default:
        return 'default';
    }
  };

  // Get status label
  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'drafted':
        return 'Drafted';
      case 'sent':
        return 'Sent';
      case 'cleared':
        return 'Cleared';
      default:
        return status;
    }
  };

  if (isFetchingInvoice) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" tip="Loading invoice data..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: 20 
      }}>
        <h1 style={{ margin: 0, fontSize: 30 }}>
          Copy Invoice
        </h1>
        
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/invoice")}
          disabled={isLoading}
          style={{ height: "40px" }}
        >
          Back
        </Button>
      </div>

      <Form 
        form={form} 
        onFinish={onFinish} 
        layout="vertical"  
        colon={false}  
        onValuesChange={(_, allValues) => {
          latestValuesRef.current = allValues;
        }}        
        initialValues={{ 
          items: [{}],
          invoice_date: dayjs(),
          due_date: dayjs().add(3, "day"),
          currency: "INR",
          cgst_percent: 9.00,
          sgst_percent: 9.00,
          igst_percent: 18,
          invoice_status: "sent",
          payment_link: "",
          email: "",
        }}
      >
        {/* HEADER */}
        <Row>
          <Col xs={24} sm={24}>
            <div style={sectionStyle}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <span style={{
                        fontFamily: "inter, sans-serif",
                        fontSize: "14px",
                        paddingLeft: "15px",
                        fontWeight: 600,
                      }}>
                        M/S.: <span className="text-red-500">*</span>
                      </span>
                    }
                    required={false}
                    name="customer_name"
                    rules={[{ required: true, message: "Please select or enter customer name" }]}
                  >
                    <Select
                      size="large"
                      loading={isLoading}
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        String(option?.label || "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onSearch={(value) => {
                        setCustomerSearchText(value);
                        debouncedSearchCustomers(value);
                      }}
                      placeholder="Select or Enter Customer Name"
                      className="CustomSelect"
                      options={customerOptions}
                      notFoundContent={
                        isLoading ? "Searching..." : "Type to add new customer"
                      }
                      onChange={(customerValue) => {
                        if (
                          typeof customerValue === "string" &&
                          customerValue.startsWith(CUSTOM_VALUE_PREFIX)
                        ) {
                          // Manual entry - mark as manual entry and store
                          setIsManualCustomerEntry(true);
                          form.setFieldsValue({
                            customer_name: customerValue,
                          });
                        } else {
                          // Existing customer selected
                          setIsManualCustomerEntry(false);
                          const relatedProject = projects.find(
                            (p: any) => p.clientId?._id === customerValue
                          );

                          if (relatedProject) {
                            form.setFieldsValue({
                              project: relatedProject._id,
                            });
                          } else {
                            form.setFieldsValue({
                              project: null,
                            });
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                
                {/* RIGHT → INVOICE NUMBER */}
                <Col xs={24} md={12}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingBottom: 20,
                    paddingTop: 10,
                    paddingRight: 7,
                    paddingLeft: 25
                  }}>
                    <span style={{
                      fontWeight: 600,
                      marginRight: 8,
                      minWidth: 120,
                      textAlign: "right"
                    }}>
                      Invoice Number :
                    </span>
                    <Row gutter={6} style={{ width: "100%" }}>
                      <Col span={6}>
                        <Input value={prefix} className="Allinput" readOnly />
                      </Col>
                      <Col span={8}>
                        <Input value={year} className="Allinput" readOnly />
                      </Col>
                      <Col span={10}>
                        <Input value={formatSequence(invoiceSeq)} className="Allinput" readOnly />
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="address"
                    rules={[
                      { required: true, message: "Please enter the address" },
                    ]}
                  >
                    <Input.TextArea
                      placeholder="Enter Customer Address..."
                      className="Allinputtextarea"
                    />
                  </Form.Item>
                  
                  <div style={{ display: "flex", alignItems: "center", paddingBottom: 20 }}>
                    <span style={{
                      fontWeight: 600,
                      marginRight: 10,
                      minWidth: 110
                    }}>
                      Place of Supply:
                    </span>
                    <Form.Item
                      name="place_of_supply"
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input placeholder="Place of supply" className="Allinput" />
                    </Form.Item>
                  </div>
                  
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{
                      fontWeight: 600,
                      marginRight: 10,
                      minWidth: 110
                    }}>
                      GSTIN NO. :
                    </span>
                    <Form.Item
                      name="gstno"
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input
                        placeholder="Enter your GST NO..."
                        className="Allinput"
                      />
                    </Form.Item>
                  </div>
                </Col>
                
                <Col xs={24} md={12}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingTop: 20,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 8,
                        minWidth: 140,
                        textAlign: "right"
                      }}
                    >
                      Invoice Date:
                    </span>
                    <Form.Item
                      name="invoice_date"
                      style={{ marginBottom: 0, width: "100%" }}
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <DatePicker
                        className="Allinput"
                        format="DD-MM-YYYY"
                        style={{ width: "100%" }}
                            disabledDate={disablePastDates}
                      />
                    </Form.Item>
                  </div>
                  
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingTop: 20,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 8,
                        minWidth: 140,
                        textAlign: "right"
                      }}
                    >
                      Due Date:
                    </span>
                    <Form.Item
                      name="due_date"
                      style={{ marginBottom: 0, width: "100%" }}
                      rules={[
                        { required: true, message: "Required" },
                        {
                          validator: (_: any, value: any) => {
                            if (!value) {
                              return Promise.resolve();
                            }
                      
                            const selectedDueDate = dayjs(value).startOf("day");
                            const selectedInvoiceDate = invoiceDate
                              ? dayjs(invoiceDate).startOf("day")
                              : null;
                      
                            if (selectedInvoiceDate && selectedDueDate.isBefore(selectedInvoiceDate)) {
                              return Promise.reject(new Error("Due date must be equal to or later than invoice date"));
                            }
                      
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <DatePicker
                        className="Allinput"
                        format="DD-MM-YYYY"
                        style={{ width: "100%" }}
                            disabledDate={disableDueDates}
                      />
                    </Form.Item>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingTop: 20,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 8,
                        minWidth: 140,
                        textAlign: "right"
                      }}
                    >
                      Currency:
                    </span>
                    <Form.Item
                      name="currency"
                      style={{ marginBottom: 0, width: "100%" }}
                    >
                      <Select
                        placeholder="Select Currency"
                        className="CustomSelect"
                        showSearch
                        optionFilterProp="children"
                        onChange={handleCurrencyChange}
                      >
                        <Select.Option value="INR">₹ INR</Select.Option>
                        {currencies.map((currency) => (
                          <Select.Option key={currency.code} value={currency.code}>
                           {currency.symbol} {currency.code} 
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  {selectedCurrency !== 'INR' && selectedCurrency && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        paddingTop: 20,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          marginRight: 8,
                          minWidth: 140,
                          textAlign: "right"
                        }}
                      >
                        Currency Conversion Rate:
                      </span>
                      <div style={{ width: "100%" }}>
                        <Input
                          size="large"
                          type="number" 
                          placeholder={`1 ${selectedCurrency} = ? INR`}
                          value={currencyState.rate || ""}
                          onChange={(e) => handleCurrencyRateChange(e.target.value)}
                          onWheel={(e) => e.currentTarget.blur()}
                          className="Allinput"
                        />
                        {currencyState.rate && (
                          <div style={{ 
                            marginTop: 18,
                            textAlign: "right",
                            fontSize: "12px",
                            color: "#1e40af",
                            fontWeight: 500,
                          }}>
                            Current Rate: 1 {selectedCurrency} = INR {currencyState.rate.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Invoice Status Field */}
                  <Form.Item name="invoice_status" hidden>
                    <Input />
                  </Form.Item>

                  {/* Display current status with tag */}
                  {invoiceStatus && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        marginTop: 10,
                      }}
                    >
                      {/* Status display can be added here if needed */}
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Items Section */}
        <Row>
          <Col xs={24} sm={24}>
            <div style={sectionStyle}>
              <Form.List name="items">
                {(fields, { add, remove }) => (
                  <>
                    {/* HEADER */}
                    <Row gutter={12} style={{
                      fontWeight: 600,
                      borderBottom: "1px solid #eee",
                      paddingBottom: 8,
                      marginBottom: 12,
                    }}>
                      <Col span={2}>Sr.No.</Col>
                      <Col span={10}>Description</Col>
                      <Col span={3}>HSN Code</Col>
                      <Col span={3}>GST %</Col>
                      <Col span={4}>
                        {selectedCurrency !== 'INR' && selectedCurrency ? 
                          `Amount (${selectedCurrency})` : 
                          "Amount (₹ INR)"}
                      </Col>
                      <Col span={2}></Col>
                    </Row>

                    {/* ITEMS */}
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Row gutter={12} key={key} align="middle">
                        <Col span={2}>
                          <Form.Item
                            name={[name, "sr_no"]}
                            initialValue={index + 1}
                          >
                            <Input value={index + 1} disabled className="Allinput" />
                          </Form.Item>
                        </Col>

                        <Col span={10}>
                          <Form.Item {...restField} name={[name, "description"]} rules={[{ required: true, message: "Required" }]}>
                            <Input placeholder="Description" className="Allinput" />
                          </Form.Item>
                        </Col>

                        <Col span={3}>
                          <Form.Item {...restField} name={[name, "hsn"]}>
                            <Input placeholder="HSN Code" className="Allinput" />
                          </Form.Item>
                        </Col>

                        <Col span={3}>
                          <Form.Item {...restField} name={[name, "gst"]}>
                            <Input placeholder="GST %" className="Allinput" />
                          </Form.Item>
                        </Col>

                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "originalAmount"]}
                          >
                            <Input 
                              placeholder={selectedCurrency !== 'INR' && selectedCurrency ? 
                                `Amount in ${selectedCurrency}` : 
                                "Amount"}
                              type="number" min={0} step="0.01" 
                              className="Allinput"
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) => {
                                const originalAmount = Math.max(0, Math.round((parseFloat(e.target.value) || 0) * 100) / 100);
                                handleItemAmountChange(name, originalAmount);
                              }}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={2} style={{ paddingBottom: "15px" }}>
                          <Button danger size="large" onClick={() => remove(name)}>
                            X
                          </Button>
                        </Col>
                      </Row>
                    ))}

                    {/* ADD ITEM BUTTON */}
                    <Row gutter={16}>
                      <Col span={10}>
                        <Button 
                          type="dashed" 
                          onClick={() => {
                            const newItem = {
                              description: "",
                              hsn: "",
                              gst: "",
                              originalAmount: 0,
                              amount: 0,
                            };
                            add(newItem);
                          }} 
                          block
                        >
                          + Add Item
                        </Button>
                      </Col>
                    </Row>
                  </>
                )}
              </Form.List>

              {/* TOTAL */}

              {/* GST NO */}
              <Row gutter={16} style={{ marginTop: 15 }}>
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ fontWeight: 600, marginRight: 10, minWidth: 110 }}>
                      GSTIN NO. :
                    </span>
                    <Form.Item name="gst_no" style={{ marginBottom: 0, flex: 1 }}>
                      <Input className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Bank and Tax Details Section */}
        <Row>
          <Col xs={24} sm={24}>
            <div style={sectionStyle}>
              {/* ROW 1 */}
              <Row gutter={16} align="middle">
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ fontWeight: 600, marginRight: 10, minWidth: 170 }}>
                      Bank Name :
                    </span>
                    <Form.Item name="bank_name" style={{ marginBottom: 0, flex: 1 }}>
                      <Input className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    padding: 20,
                    paddingRight: "80px",
                  }}>
                    <span style={{
                      fontWeight: 600,
                      marginRight: 10,
                      minWidth: 140,
                      textAlign: "right",
                    }}>
                      Taxable Amount :
                    </span>
                    <Form.Item name="taxable_amount" style={{ marginBottom: 0, width: 200 }}>
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>

              {/* ROW 2 */}
              <Row gutter={16} align="middle">
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ fontWeight: 600, marginRight: 10, minWidth: 170 }}>
                      Bank Account Number :
                    </span>
                    <Form.Item name="bank_account_number" style={{ marginBottom: 0, flex: 1 }}>
                      <Input className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  {/* CGST */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    marginBottom: 8,
                    paddingTop: 18,
                    paddingRight: "80px",
                  }}>
                    <span style={{ fontWeight: 600, marginRight: 10, minWidth: 90 }}>
                      CGST % :
                    </span>
                    <Form.Item name="cgst_percent" style={{ marginBottom: 0, width: 80 }}>
                      <Input 
                        type="number" min={0} step="0.01" 
                        className="Allinput" 
                       // readOnly={selectedCurrency !== "INR"}
                      />
                    </Form.Item>
                    <span style={{ margin: "0 10px", fontWeight: 600 }}>Amount :</span>
                    <Form.Item name="cgst_amount" style={{ marginBottom: 0, width: 120 }}>
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>

                  {/* SGST */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingTop: 10,
                    paddingRight: "80px",
                  }}>
                    <span style={{ fontWeight: 600, marginRight: 10, minWidth: 90 }}>
                      SGST % :
                    </span>
                    <Form.Item name="sgst_percent" style={{ marginBottom: 0, width: 80 }}>
                      <Input 
                        type="number" min={0} step="0.01" 
                        className="Allinput" 
                       //readOnly={selectedCurrency !== "INR"}
                      />
                    </Form.Item>
                    <span style={{ margin: "0 10px", fontWeight: 600 }}>Amount :</span>
                    <Form.Item name="sgst_amount" style={{ marginBottom: 0, width: 120 }}>
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>

              {/* ROW 3 */}
              <Row gutter={16} align="middle">
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center", paddingTop: 20 }}>
                    <span style={{ fontWeight: 600, marginRight: 10, minWidth: 170 }}>
                      IFSC Code :
                    </span>
                    <Form.Item name="ifsc_code" style={{ marginBottom: 0, flex: 1 }}>
                      <Input className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingTop: 18,
                    paddingRight: "80px",
                  }}>
                    <span style={{ fontWeight: 600, marginRight: 10, minWidth: 90 }}>
                      IGST % :
                    </span>
                    <Form.Item name="igst_percent" style={{ marginBottom: 0, width: 80 }}>
                      <Input 
                        type="number" min={0} step="0.01" 
                        className="Allinput" 
                        //readOnly={selectedCurrency !== "INR"}
                      />
                    </Form.Item>
                    <span style={{ margin: "0 10px", fontWeight: 600 }}>Amount :</span>
                    <Form.Item name="igst_amount" style={{ marginBottom: 0, width: 120 }}>
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>

              {/* Payment Link Field */}
              <Row gutter={16} align="middle" style={{ marginTop: 20 }}>
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ fontWeight: 600, marginRight: 10, minWidth: 170 }}>
                      Payment Link :
                    </span>
                    <Form.Item
                      name="payment_link"
                      style={{ marginBottom: 0, flex: 1 }}
                      rules={[
                        {
                          type: 'url',
                          message: 'Please enter a valid URL',
                        },
                      ]}
                    >
                      <Input 
                        placeholder="Enter payment link (e.g., https://pay.example.com/invoice/123)" 
                        className="Allinput" 
                      />
                    </Form.Item>
                    <Button
                      type="default"
                      style={{ marginLeft: 8 }}
                      onClick={() => {
                        const paymentLink = form.getFieldValue("payment_link");
                        if (!paymentLink) {
                          message.warning("No payment link to copy");
                          return;
                        }
                        const fallbackInput = document.createElement("textarea");
                        fallbackInput.value = paymentLink;
                        fallbackInput.setAttribute("readonly", "");
                        fallbackInput.style.position = "absolute";
                        fallbackInput.style.left = "-9999px";
                        document.body.appendChild(fallbackInput);
                        fallbackInput.select();
                        try {
                          const copied = document.execCommand("copy");
                          document.body.removeChild(fallbackInput);
                          if (copied) {
                            message.success("Payment link copied");
                          } else {
                            message.error("Failed to copy payment link");
                          }
                        } catch {
                          document.body.removeChild(fallbackInput);
                          message.error("Failed to copy payment link");
                        }
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </Col>
              </Row>

              {/* Email Field */}
              <Row gutter={16} align="middle" style={{ marginTop: 20 }}>
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ fontWeight: 600, marginRight: 10, minWidth: 170 }}>
                      Email :
                    </span>
                    <Form.Item
                      name="email"
                      style={{ marginBottom: 0, flex: 1 }}
                      normalize={(value) => value?.toLowerCase()}
                      rules={[
                        {
                          type: "email",
                          message: "Please enter a valid email address",
                        },
                      ]}
                    >
                      <Input placeholder="Enter email address" className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
              </Row>

              {/* ROUND OFF */}
              <Row style={{ marginTop: 10 }} justify="end">
                <Col xs={24} md={12}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingRight: "80px",
                    paddingTop: 18
                  }}>
                    <span style={{ fontWeight: 600, marginRight: 12 }}>
                      Round Off :
                    </span>
                    <Form.Item name="round_off" style={{ marginBottom: 0, width: 180 }}>
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>

              {/* GRAND TOTAL */}
              <Row style={{ marginTop: 20 }} justify="end">
                <Col xs={24} md={12}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingRight: "80px",
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 16, marginRight: 12 }}>
                      Grand Total :
                    </span>
                    <Form.Item name="grand_total" style={{ marginBottom: 0, width: 180 }}>
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>

              {/* ACTION BUTTONS */}
              <Row justify="end">
                <Col xs={24} sm={24} md={24} xl={24} xxl={20} className="d-flex justify-content-end me-4">
                  <div className="button-wrapper" style={{ marginTop: 80 }}>
                    <button
                      onClick={handlePreview}
                      type="button"
                      disabled={isLoading}
                      style={{
                        background: "#fff",
                        border: "1px solid #d9d9d9",
                        height: "40px",
                        padding: "0 16px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "10px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#fff";
                      }}
                    >
                      Preview
                    </button>

                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={handleSaveDraft}
                      style={{
                        background: "#f8fafc",
                        border: "1px solid #cbd5e1",
                        color: "#334155",
                        height: "40px",
                        padding: "0 16px",
                        borderRadius: "6px",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "10px",
                        opacity: isLoading ? 0.7 : 1,
                      }}
                    >
                      {submitMode === "draft" ? "Saving Draft..." : "Save as a Draft"}
                    </button>

                    <button
                      className="allsubmitebutton w-20"
                      type="submit"
                      disabled={isLoading}
                    >
                      {submitMode === "save" ? "Saving..." : "Save"}
                    </button>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CopyInvoicePage;









































































